import mapboxgl from 'mapbox-gl';
import { createUltraSmoothPath } from './smoothPath.js';
import shipIcon from 'url:../images/ship_icon.svg';

// Mapbox access token
mapboxgl.accessToken = 'pk.eyJ1IjoiZHBsYW1hZGVhbGEtdHdpa2kiLCJhIjoiY20yYWdpaXIzMGhoNDJqcjQ4cXp1bmlyYiJ9.oUapETjTHrjxBme07CDvoA';

const BASE_URL = process.env.VESSEL_LOCATOR_URL || "https://tpact-vessel-locator.prod.twiki-powered.com";
// const BASE_URL = "http://localhost:3000"

let map;

// initialize Mapbox map and fetch vessel data
export async function initializeMap() {
  // clear the map container to avoid the warning
  const mapContainer = document.getElementById("map");
  mapContainer.innerHTML = "";
  
  // create spinner overlay
  const spinnerOverlay = document.createElement("div");
  spinnerOverlay.id = "spinner-overlay";
  spinnerOverlay.innerHTML = `<div class="spinner"></div>`;
  mapContainer.style.position = "relative";
  mapContainer.appendChild(spinnerOverlay);

  // get subscription ID from URL
  const subscriptionId = window.location.pathname.slice(1);

  if (!subscriptionId) {
    const errorOverlay = createErrorOverlay("No subscription ID provided.");
    mapContainer.appendChild(errorOverlay);
    spinnerOverlay.remove();
    return;
  }

  try {
    // initialize map
    map = new mapboxgl.Map({
      container: 'map',
      style: 'mapbox://styles/mapbox/light-v11', // clean gray/white 2D map
      center: [4.47917, 51.9225], // [lng, lat]
      zoom: 4,
      minZoom: 2,
      maxZoom: 2.99,
      antialias: true, // enable antialiasing for smooth rendering
      pitch: 0,     // force 2D view
      bearing: 0,   // no rotation
      fadeDuration: 300,
      attributionControl: false,
      renderWorldCopies: false, // prevent world wrapping
      projection: 'mercator' // force flat 2D projection instead of globe
    });

    // add custom controls (only zoom, no pitch/rotation for 2D)
    map.addControl(new mapboxgl.NavigationControl({
      showCompass: false,  // hide compass for 2D
      showZoom: true,      // keep zoom controls
      visualizePitch: false // no pitch visualization
    }), 'top-right');
    map.addControl(new mapboxgl.AttributionControl({
      compact: true
    }), 'bottom-right');

    // disable 3D interactions
    map.dragRotate.disable();
    map.touchZoomRotate.disableRotation();
    
    // wait for map to load before fetching data
    map.on('load', async () => {
      await setupMapLayers();
      await fetchAndRenderVesselData(subscriptionId);
      addLegend();
      spinnerOverlay.remove();
    });

    // handle window resize to prevent purple areas
    window.addEventListener('resize', () => {
      if (map) {
        map.resize();
      }
    });

    // handle macOS double-click titlebar maximize/restore with proper timing
    let resizeTimeout;
    const handleResize = () => {
      if (map) {
        map.resize();
      }
    };
    
    const observer = new ResizeObserver(() => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(handleResize, 100);      
    });
    
    observer.observe(document.getElementById('map'));

    return map;
  } catch (error) {
    console.error('Error initializing map:', error);
    const errorOverlay = createErrorOverlay("Failed to initialize map.");
    mapContainer.appendChild(errorOverlay);
    spinnerOverlay.remove();
  }
}

// fetch and render vessel data from API
async function fetchAndRenderVesselData(subscriptionId) {
  try {
    const response = await fetch(`${BASE_URL}/api/vessel/${subscriptionId}`);
    const data = await response.json();

    // Log the data structure for debugging
    let debug = false;
    if (debug) {
      console.log("API response data:", {
        hasCurrentPath: data.current_path && data.current_path.length > 0,
        hasPredictedPath: data.predicted_path && data.predicted_path.length > 0,
        currentPathLength: data.current_path?.length || 0,
        predictedPathLength: data.predicted_path?.length || 0,
        vesselInfo: data.vessel ? true : false,
        polInfo: data.pol ? true : false,
        podInfo: data.pod ? true : false,
        vesselStructure: data.vessel ? Object.keys(data.vessel) : 'no vessel data'
      });

      console.log("Full vessel data:", data.vessel);
    }

    // check for insufficient data
    if (data.status === "insufficient_data") {
      const message = getErrorMessage(data.error.code);
      const errorOverlay = createErrorOverlay(message);
      document.getElementById("map").appendChild(errorOverlay);
      return;
    }

    // add ports to map
    await addPortsToMap(data);

    // add vessel paths
    await addVesselPaths(data);

    // add vessel marker
    await addVesselMarker(data);

    // fit map to show all data
    fitMapToData(data);

  } catch (error) {
    console.error('Error fetching vessel data:', error);
    const errorOverlay = createErrorOverlay("Failed to fetch vessel data.");
    document.getElementById("map").appendChild(errorOverlay);
  }
}

// add ports (POL, POD, POTs) to map
async function addPortsToMap(data) {
  const ports = [];
  
  // add POL
  if (data.pol) {
    let departureInfo = "";
    
    // check if pol.data has departure dates
    if (data.pol.estimated_departure_date || data.pol.actual_departure_date) {
      const etd = data.pol.estimated_departure_date || "N/A";
      const atd = data.pol.actual_departure_date || "";
      
      if (atd) {
        departureInfo = `
          <div class="popup-info-row">
            <span class="popup-label">ATD:</span>
            <span class="popup-value">${formatTimestampEU(atd)}</span>
          </div>
        `;
      } else if (etd && etd !== "N/A") {
        departureInfo = `
          <div class="popup-info-row">
            <span class="popup-label">ETD:</span>
            <span class="popup-value">${formatTimestampEU(etd)}</span>
          </div>
        `;
      }
    } else if (data.journeys && data.journeys.length > 0) {
      // fallback to journey data if pol.data is not available
      const journey = data.journeys[0]; // first journey for POL
      const etd = journey.estimated_departure_date || "N/A";
      const atd = journey.actual_departure_date || "";
      
      if (atd) {
        departureInfo = `
          <div class="popup-info-row">
            <span class="popup-label">ATD:</span>
            <span class="popup-value">${formatTimestampEU(atd)}</span>
          </div>
        `;
      } else if (etd && etd !== "N/A") {
        departureInfo = `
          <div class="popup-info-row">
            <span class="popup-label">ETD:</span>
            <span class="popup-value">${formatTimestampEU(etd)}</span>
          </div>
        `;
      }
    }
    
    const popupContent = `
      <div class="popup-container">
        <div class="popup-header">
          <div class="popup-badge pol-badge">POL</div>
          <div class="popup-title">${data.pol.port_name}</div>
        </div>
        <div class="popup-content">
          ${departureInfo}
          <div class="popup-info-row">
            <span class="popup-label">Type:</span>
            <span class="popup-value">Port of Loading</span>
          </div>
        </div>
      </div>
    `;
    
    ports.push({
      label: "POL",
      name: data.pol.port_name,
      coord: [data.pol.lon, data.pol.lat], // [lng, lat] for Mapbox
      color: "#337AB7",
      type: "pol",
      popupContent: popupContent
    });
  }

  // add transshipment ports
  if (data.transshipment_ports && data.transshipment_ports.length > 0) {
    data.transshipment_ports.forEach((port, index) => {
      const popupContent = `
        <div class="popup-container">
          <div class="popup-header">
            <div class="popup-badge pot-badge">POT${index + 1}</div>
            <div class="popup-title">${port.port_name}</div>
          </div>
          <div class="popup-content">
            <div class="popup-info-row">
              <span class="popup-label">Type:</span>
              <span class="popup-value">Transshipment Port</span>
            </div>
          </div>
        </div>
      `;
      
      ports.push({
        label: "POT" + (index + 1),
        name: port.port_name,
        coord: [port.lon, port.lat],
        color: "#337AB7",
        type: "transshipment",
        popupContent: popupContent
      });
    });
  }

  // add POD
  if (data.pod) {
    let arrivalInfo = "";
    
    // check if pod.data has arrival dates
    if (data.pod.estimated_arrival_date || data.pod.actual_arrival_date) {
      const eta = data.pod.estimated_arrival_date || "N/A";
      const ata = data.pod.actual_arrival_date || "";
      
      if (ata) {
        arrivalInfo = `
          <div class="popup-info-row">
            <span class="popup-label">ATA:</span>
            <span class="popup-value">${formatTimestampEU(ata)}</span>
          </div>
        `;
      } else if (eta && eta !== "N/A") {
        arrivalInfo = `
          <div class="popup-info-row">
            <span class="popup-label">ETA:</span>
            <span class="popup-value">${formatTimestampEU(eta)}</span>
          </div>
        `;
      }
    } else if (data.journeys && data.journeys.length > 0) {
      // fallback to journey data if pod.data is not available
      const journey = data.journeys[data.journeys.length - 1];
      const eta = journey.estimated_arrival_date || "N/A";
      arrivalInfo = `
        <div class="popup-info-row">
          <span class="popup-label">ETA:</span>
          <span class="popup-value">${formatTimestampEU(eta)}</span>
        </div>
      `;
    }
    
    const popupContent = `
      <div class="popup-container">
        <div class="popup-header">
          <div class="popup-badge pod-badge">POD</div>
          <div class="popup-title">${data.pod.port_name}</div>
        </div>
        <div class="popup-content">
          ${arrivalInfo}
          <div class="popup-info-row">
            <span class="popup-label">Type:</span>
            <span class="popup-value">Port of Discharge</span>
          </div>
        </div>
      </div>
    `;
    
    ports.push({
      label: "POD",
      name: data.pod.port_name,
      coord: [data.pod.lon, data.pod.lat],
      color: "#337AB7",
      type: "pod",
      popupContent: popupContent
    });
  }

  // add ports to map
  ports.forEach(port => {
    // create marker element
    const el = document.createElement('div');
    el.className = 'port-marker';
    el.style.backgroundColor = port.color;
    el.style.width = '20px';
    el.style.height = '20px';
    el.style.borderRadius = '50%';
    el.style.border = '2px solid white';
    el.style.cursor = 'pointer';
    el.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';

    // add label
    const label = document.createElement('div');
    label.textContent = port.label;
    label.style.position = 'absolute';
    label.style.top = '-30px';
    label.style.left = '50%';
    label.style.transform = 'translateX(-50%)';
    label.style.backgroundColor = 'white';
    label.style.padding = '2px 6px';
    label.style.borderRadius = '4px';
    label.style.fontSize = '12px';
    label.style.fontWeight = 'bold';
    label.style.color = port.color;
    label.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
    el.appendChild(label);

    // create popup content with fallback
    let popupContent = port.popupContent || `
      <div class="popup-container">
        <div class="popup-header">
          <div class="popup-badge">${port.label}</div>
          <div class="popup-title">${port.name}</div>
        </div>
      </div>
    `;

    // add marker to map
    new mapboxgl.Marker(el)
      .setLngLat(port.coord)
      .setPopup(new mapboxgl.Popup({ 
        offset: 25,
        className: 'beautiful-popup',
        closeButton: true,
        closeOnClick: false,
        maxWidth: '300px'
      }).setHTML(popupContent))
      .addTo(map);
  });
}

// setup map layers for rendering
async function setupMapLayers() {
  // add source for vessel paths
  map.addSource('vessel-paths', {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: []
    },
    lineMetrics: true // enable line metrics for gradients
  });

  // add ultra-smooth current path layer
  map.addLayer({
    id: 'current-path',
    type: 'line',
    source: 'vessel-paths',
    filter: ['==', ['get', 'type'], 'current'],
    layout: {
      'line-join': 'round',
      'line-cap': 'round'
    },
    paint: {
      'line-color': '#1982c4',
      'line-width': {
        base: 1.5,
        stops: [[8, 2], [16, 4], [22, 6]]
      },
      'line-opacity': 0.9,
      'line-blur': 0.5 // subtle blur for ultra-smooth appearance
    }
  });

  // add predicted path layer with dashed style
  map.addLayer({
    id: 'predicted-path',
    type: 'line',
    source: 'vessel-paths',
    filter: ['==', ['get', 'type'], 'predicted'],
    layout: {
      'line-join': 'round',
      'line-cap': 'round'
    },
    paint: {
      'line-color': '#ff802d',
      'line-width': {
        base: 1.5,
        stops: [[8, 2], [16, 4], [22, 6]]
      },
      'line-opacity': 0.9,
      'line-dasharray': [2, 2],
      'line-blur': 0.5
    }
  });

  // add animated gradient layer for current path
  map.addLayer({
    id: 'current-path-gradient',
    type: 'line',
    source: 'vessel-paths',
    filter: ['==', ['get', 'type'], 'current'],
    layout: {
      'line-join': 'round',
      'line-cap': 'round'
    },
    paint: {
      'line-color': '#1982c4',
      'line-width': {
        base: 1.5,
        stops: [[8, 1], [16, 2], [22, 3]]
      },
      'line-gradient': [
        'interpolate',
        ['linear'],
        ['line-progress'],
        0, 'rgba(25, 130, 196, 0.1)',
        0.5, 'rgba(25, 130, 196, 0.8)',
        1, 'rgba(25, 130, 196, 1)'
      ],
      'line-opacity': 0.7
    }
  });

}

// add vessel paths to map with ultra-smooth rendering
async function addVesselPaths(data) {
  const features = [];

  // process current path
  if (data.current_path && data.current_path.length > 0) {
    const currentPathFeature = processPathData(data.current_path, 'current');
    if (currentPathFeature) features.push(currentPathFeature);
  }

  // process predicted path
  if (data.predicted_path && data.predicted_path.length > 0) {
    const predictedPathFeature = processPathData(data.predicted_path, 'predicted');
    if (predictedPathFeature) features.push(predictedPathFeature);
  }

  // update paths source
  map.getSource('vessel-paths').setData({
    type: 'FeatureCollection',
    features: features
  });
}

// add vessel marker to map
async function addVesselMarker(data) {
  // check multiple possible vessel data structures
  let vesselData = null;
  let vesselPosition = null;
  
  if (data.vessel && data.vessel.position && data.vessel.position.lat && data.vessel.position.lon) {
    // structure: data.vessel.position.lat/lon
    vesselData = data.vessel;
    vesselPosition = [data.vessel.position.lon, data.vessel.position.lat];
  } else if (data.vessel && data.vessel.lat && data.vessel.lon) {
    // structure: data.vessel.lat/lon
    vesselData = data.vessel;
    vesselPosition = [data.vessel.lon, data.vessel.lat];
  } else {
    console.log('No vessel position found in data:', data);
    return;
  }

  // get vessel name from various possible locations
  const vesselName = vesselData.vessel_name || vesselData.name || 'Vessel';
  const vesselSpeed = vesselData.position.speed || 'N/A';
  const vesselCourse = vesselData.position.heading || 'N/A';
  const vesselTimestamp = (vesselData.position && vesselData.position.timestamp) || vesselData.timestamp || new Date().toISOString();

  // create the beautiful vessel marker
  const vesselMarkerElement = document.createElement('div');
  vesselMarkerElement.className = 'vessel-marker';
  vesselMarkerElement.innerHTML = `
    <div class="vessel-marker-container">
      <div class="vessel-ripple vessel-ripple-1"></div>
      <div class="vessel-ripple vessel-ripple-2"></div>
      <div class="vessel-ripple vessel-ripple-3"></div>
      <div class="vessel-icon-container">
        <img src="${shipIcon}" alt="Vessel" class="vessel-icon-img">
      </div>
    </div>
  `;

  // add the custom marker to the map
  new mapboxgl.Marker(vesselMarkerElement, {
    anchor: 'center'
  })
    .setLngLat(vesselPosition)
    .setPopup(new mapboxgl.Popup({ 
      offset: 25,
      className: 'beautiful-popup vessel-popup',
      maxWidth: '320px'
    }).setHTML(`
      <div class="popup-container">
        <div class="popup-header">
        <div class="popup-badge">
          <img src="${shipIcon}" alt="Vessel" class="vessel-icon-img">
        </div>
        <div class="popup-title">${vesselName}</div>
        </div>
        <div class="popup-content">
          <div class="popup-info-row">
            <span class="popup-label">MMSI:</span>
            <span class="popup-value">${vesselData.mmsi || 'N/A'}</span>
          </div>
          <div class="popup-info-row">
            <span class="popup-label">Position:</span>
            <span class="popup-value">${vesselPosition[1].toFixed(4)}, ${vesselPosition[0].toFixed(4)}</span>
          </div>
          <div class="popup-info-row">
            <span class="popup-label">Speed:</span>
            <span class="popup-value">${vesselSpeed} knots</span>
          </div>
          <div class="popup-info-row">
            <span class="popup-label">Course:</span>
            <span class="popup-value">${vesselCourse}°</span>
          </div>
          <div class="popup-info-row">
            <span class="popup-label">Last Update:</span>
            <span class="popup-value">${formatTimestampEU(vesselTimestamp) || 'N/A'}</span>
          </div>
          <div class="popup-info-row">
            <span class="popup-label">Source:</span>
            <span class="popup-value">${vesselData.source || 'N/A'}</span>
          </div>
        </div>
      </div>
    `))
    .addTo(map);
}

// process path data with ultra-smooth rendering
function processPathData(pathData, type) {
  try {
    let allCoordinates = [];
    
    // handle multiple segments (when POTs are present)
    if (pathData.length > 0 && Array.isArray(pathData[0]) && Array.isArray(pathData[0][0])) {

      // flatten all segments into a single coordinate array
      pathData.forEach((segment, index) => {
        // convert segment coordinates to {coord: [lat, lon]} format
        const segmentCoords = segment.map(coord => ({ coord }));
        
        // add to all coordinates (avoid duplicating connection points)
        if (index === 0) {
          allCoordinates = allCoordinates.concat(segmentCoords);
        } else {
          // skip first coordinate of subsequent segments to avoid duplication
          allCoordinates = allCoordinates.concat(segmentCoords.slice(1));
        }
      });
    } 
    // handle single segment (no POTs)
    else if (pathData.length > 0 && Array.isArray(pathData[0])) {
      allCoordinates = pathData.map(coord => ({ coord }));
    }
    // handle already processed format
    else {
      allCoordinates = pathData;
    }
    
    console.log(`Total coordinates for ${type} path:`, allCoordinates.length);
    
    if (allCoordinates.length < 2) {
      console.log(`${type} path has insufficient coordinates:`, allCoordinates.length);
      return null;
    }
    
    // extract coordinates
    const rawCoordinates = allCoordinates.map(point => point.coord);
    
    // create ultra-smooth path
    const smoothedCoordinates = createUltraSmoothPath(rawCoordinates);
    
    // convert to GeoJSON LineString (note: Mapbox uses [lng, lat] format)
    const geojsonCoordinates = smoothedCoordinates.map(coord => [coord[1], coord[0]]);
    
    // create feature
    const feature = {
      type: 'Feature',
      properties: { type },
      geometry: {
        type: 'LineString',
        coordinates: geojsonCoordinates
      }
    };
    
    console.log(`Successfully created ${type} path feature with`, geojsonCoordinates.length, 'coordinates');
    return feature;
    
  } catch (error) {
    console.error(`Error processing ${type} path:`, error);
    return null;
  }
}

// Report issue to backend API
function reportIssue(issueDescription, subscriptionId) {
  fetch(`${BASE_URL}/api/issue`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      description: issueDescription,
      subscription_id: subscriptionId,
    }),
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error("Failed to report issue");
      }
      return response.json();
    })
    .then((data) => {
      console.log("Issue reported successfully:", data);
    })
    .catch((err) => {
      console.error("Error reporting issue:", err);
    });
}

// create error overlay with interactive report button
function createErrorOverlay(message) {
  const overlay = document.createElement("div");
  overlay.className = "error-overlay";
  overlay.innerHTML = `
    <div class="error-content">
      <h3>Error</h3>
      <p>${message}</p>
      <button class="report-issue-button">Report Issue</button>
    </div>
  `;

  const reportButton = overlay.querySelector(".report-issue-button");
  reportButton.addEventListener("click", () => {
    reportIssue(message, window.location.pathname.slice(1));
    reportButton.textContent = "Reported!";
    reportButton.disabled = true;

    setTimeout(() => {
      reportButton.textContent = "Report Issue";
      reportButton.disabled = false;
    }, 2000);
  });

  return overlay;
}

// format timestamp for EU format
function formatTimestampEU(timestamp) {
  if (!timestamp || timestamp === 'N/A') return 'N/A';
  
  try {
    const date = new Date(timestamp);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'UTC'
    });
  } catch (error) {
    console.log(error);
    return timestamp;
  }
}


// fit map to show all data
function fitMapToData(data) {
  try {
    const bounds = new mapboxgl.LngLatBounds();
    
    // add current path bounds
    if (data.current_path && data.current_path.length > 0) {
      const pathData = Array.isArray(data.current_path[0]) ? data.current_path[0] : data.current_path;
      pathData.forEach(point => {
        if (point.coord) {
          bounds.extend([point.coord[1], point.coord[0]]);
        }
      });
    }
    
    // add predicted path bounds
    if (data.predicted_path && data.predicted_path.length > 0) {
      const pathData = Array.isArray(data.predicted_path[0]) ? data.predicted_path[0] : data.predicted_path;
      pathData.forEach(point => {
        if (point.coord) {
          bounds.extend([point.coord[1], point.coord[0]]);
        }
      });
    }
    
    // add vessel position (check multiple possible structures)
    if (data.vessel && data.vessel.position && data.vessel.position.lat && data.vessel.position.lon) {
      bounds.extend([data.vessel.position.lon, data.vessel.position.lat]);
    } else if (data.vessel && data.vessel.lat && data.vessel.lon) {
      bounds.extend([data.vessel.lon, data.vessel.lat]);
    }
    
    // add port positions
    if (data.pol && data.pol.lat && data.pol.lon) {
      bounds.extend([data.pol.lon, data.pol.lat]);
    }
    
    if (data.pod && data.pod.lat && data.pod.lon) {
      bounds.extend([data.pod.lon, data.pod.lat]);
    }
    
    if (data.transshipment_ports && data.transshipment_ports.length > 0) {
      data.transshipment_ports.forEach(port => {
        if (port.lat && port.lon) {
          bounds.extend([port.lon, port.lat]);
        }
      });
    }
    
    // fit bounds with animation
    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, {
        padding: 50,
        duration: 2000,
        easing: t => t * (2 - t) // ease out
      });
    }
    
  } catch (error) {
    console.error('Error fitting map to data:', error);
  }
}

// add legend to map
function addLegend() {
  const legendContainer = document.createElement('div');
  legendContainer.className = 'map-legend';
  
  legendContainer.innerHTML = `
    <div class="legend-header" id="legend-toggle">
      <h4>Legend</h4>
      <div class="legend-toggle-icon">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="6,9 12,15 18,9"></polyline>
        </svg>
      </div>
    </div>
    <div class="legend-content" id="legend-content">
      <div class="legend-item">
        <div class="legend-icon-container">
          <div class="legend-icon vessel-icon">
            <img src="${shipIcon}" alt="Vessel" class="legend-vessel-icon">
          </div>
        </div>
        <span class="legend-label">Vessel Position</span>
      </div>
      <div class="legend-item">
        <div class="legend-icon-container">
          <div class="legend-icon port-icon"></div>
        </div>
        <span class="legend-label">Port Marker (POL, POD, POT)</span>
      </div>
      <div class="legend-item">
        <div class="legend-icon-container">
          <div class="legend-icon current-path-icon"></div>
        </div>
        <span class="legend-label">Current Path</span>
      </div>
      <div class="legend-item">
        <div class="legend-icon-container">
          <div class="legend-icon predicted-path-icon"></div>
        </div>
        <span class="legend-label">Predicted Path</span>
      </div>
    </div>
  `;
  
  // add collapse/expand functionality
  const toggleButton = legendContainer.querySelector('#legend-toggle');
  const content = legendContainer.querySelector('#legend-content');
  const toggleIcon = legendContainer.querySelector('.legend-toggle-icon svg');
  let isCollapsed = false;
  
  toggleButton.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    isCollapsed = !isCollapsed;
    
    if (isCollapsed) {
      // collapse
      content.style.maxHeight = content.scrollHeight + 'px';
      content.style.opacity = '1';
      requestAnimationFrame(() => {
        content.style.maxHeight = '0px';
        content.style.opacity = '0';
        content.style.paddingTop = '0px';
        content.style.paddingBottom = '0px';
      });
      toggleIcon.style.transform = 'rotate(-90deg)';
      legendContainer.classList.add('collapsed');
    } else {
      // expand
      content.style.maxHeight = '0px';
      content.style.opacity = '0';
      content.style.paddingTop = '';
      content.style.paddingBottom = '';
      requestAnimationFrame(() => {
        content.style.maxHeight = content.scrollHeight + 'px';
        content.style.opacity = '1';
      });
      
      // reset max-height after animation completes
      setTimeout(() => {
        if (!isCollapsed) {
          content.style.maxHeight = '';
        }
      }, 300);
      
      toggleIcon.style.transform = 'rotate(0deg)';
      legendContainer.classList.remove('collapsed');
    }
  });
  
  // prevent clicks on the entire legend from going through to the map
  legendContainer.addEventListener('click', (e) => {
    e.stopPropagation();
  });
  
  // add legend as a Mapbox control
  const legendControl = {
    onAdd: function(map) {
      this._map = map;
      this._container = legendContainer;
      return this._container;
    },
    
    onRemove: function() {
      this._container.parentNode.removeChild(this._container);
      this._map = undefined;
    }
  };
  
  map.addControl(legendControl, 'bottom-left');
}

// add zoom debug display
if (process.env.NODE_ENV === 'dev') {
  addZoomDebug();
}
function addZoomDebug() {
  const debugContainer = document.createElement('div');
  debugContainer.className = 'zoom-debug';
  debugContainer.innerHTML = `Zoom: ${map.getZoom().toFixed(2)}`;
  
  // add debug as a Mapbox control
  const debugControl = {
    onAdd: function(map) {
      this._map = map;
      this._container = debugContainer;
      
      // update zoom level on zoom change
      map.on('zoom', () => {
        debugContainer.innerHTML = `Zoom: ${map.getZoom().toFixed(2)}`;
      });
      
      return this._container;
    },
    
    onRemove: function() {
      this._container.parentNode.removeChild(this._container);
      this._map = undefined;
    }
  };
  
  map.addControl(debugControl, 'top-left');
}

// get user-friendly error message from error code
function getErrorMessage(errorCode) {
  const errorMessages = {
    'MISSING_PORT_INFO': 'unable to retrieve complete port information for this journey.',
    'VESSEL_MAPPING_NOT_FOUND': 'vessel mapping data not found or outdated.',
  };

  return errorMessages[errorCode] || 'an unexpected error occurred while loading vessel data.';
}

// cleanup function
export function cleanup() {
  if (map) {
    map.remove();
  }
}